<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTablesStore } from 'dashboard/stores/tables';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  tableId: { type: Number, required: true },
  columns: { type: Array, required: true },
});

const { t } = useI18n();
const tablesStore = useTablesStore();

const emptyDraft = () => ({
  subject: '',
  can_read: true,
  can_create_rows: false,
  can_delete_rows: false,
  can_raw_query: false,
  writable_columns: [],
});

const draft = ref(emptyDraft());
const errorMessage = ref('');
// Set while editing an existing grant; a grant's subject is immutable once
// created, so editing reuses the same form but never touches `subject`.
const editingId = ref(null);

// Humans, bots and teams are all valid subjects through the same mechanism;
// they are grouped here only so an administrator can tell which is which.
const subjectOptions = computed(() => [
  ...tablesStore.subjects.users.map(user => ({
    value: `User:${user.id}`,
    label: user.name,
    group: t('TABLES.GRANTS.USERS'),
  })),
  ...tablesStore.subjects.bot_users.map(bot => ({
    value: `User:${bot.id}`,
    label: bot.name,
    group: t('TABLES.GRANTS.BOTS'),
  })),
  ...tablesStore.subjects.teams.map(team => ({
    value: `Team:${team.id}`,
    label: team.name,
    group: t('TABLES.GRANTS.TEAMS'),
  })),
]);

const toggleColumn = key => {
  const current = draft.value.writable_columns;
  draft.value.writable_columns = current.includes(key)
    ? current.filter(item => item !== key)
    : [...current, key];
};

const save = async () => {
  if (!draft.value.subject) return;
  errorMessage.value = '';

  const payload = {
    can_read: draft.value.can_read,
    can_create_rows: draft.value.can_create_rows,
    can_delete_rows: draft.value.can_delete_rows,
    can_raw_query: draft.value.can_raw_query,
    writable_columns: draft.value.writable_columns,
  };

  try {
    if (editingId.value) {
      await tablesStore.updateGrant(props.tableId, editingId.value, payload);
    } else {
      const [subjectType, subjectId] = draft.value.subject.split(':');
      await tablesStore.createGrant(props.tableId, {
        ...payload,
        subject_type: subjectType,
        subject_id: Number(subjectId),
      });
    }
    editingId.value = null;
    draft.value = emptyDraft();
  } catch (error) {
    errorMessage.value = error?.response?.data?.message || t('TABLES.ERROR');
  }
};

// A grant's writable columns and row permissions commonly need to change
// after it was first issued — most often because a column was added to the
// table later — so editing loads it back into the same form `save` uses.
const edit = grant => {
  errorMessage.value = '';
  editingId.value = grant.id;
  draft.value = {
    subject: `${grant.subject_type}:${grant.subject_id}`,
    can_read: grant.can_read,
    can_create_rows: grant.can_create_rows,
    can_delete_rows: grant.can_delete_rows,
    can_raw_query: grant.can_raw_query,
    writable_columns: [...grant.writable_columns],
  };
};

const cancelEdit = () => {
  editingId.value = null;
  draft.value = emptyDraft();
};

const remove = async grant => {
  if (editingId.value === grant.id) cancelEdit();
  await tablesStore.deleteGrant(props.tableId, grant.id);
};

onMounted(async () => {
  await tablesStore.fetchGrants(props.tableId);
  await tablesStore.fetchSubjects(props.tableId);
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <table class="w-full text-sm">
      <thead>
        <tr class="text-left text-n-slate-11">
          <th class="py-1">{{ t('TABLES.GRANTS.SUBJECT') }}</th>
          <th class="py-1">{{ t('TABLES.GRANTS.WRITABLE') }}</th>
          <th class="py-1">{{ t('TABLES.GRANTS.ROWS') }}</th>
          <th class="py-1" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="grant in tablesStore.grants" :key="grant.id" class="border-t border-n-weak">
          <td class="py-1 pr-2 text-n-slate-12">
            {{ grant.subject_name }}
            <span class="text-xs text-n-slate-11">({{ grant.subject_type }})</span>
          </td>
          <td class="py-1 pr-2 text-xs text-n-slate-11">
            {{ grant.writable_columns.length ? grant.writable_columns.join(', ') : '—' }}
          </td>
          <td class="py-1 pr-2 text-xs text-n-slate-11">
            <span v-if="grant.can_create_rows">{{ t('TABLES.GRANTS.CREATE') }}</span>
            <span v-if="grant.can_delete_rows"> · {{ t('TABLES.GRANTS.DELETE') }}</span>
            <span v-if="grant.can_raw_query"> · {{ t('TABLES.GRANTS.RAW') }}</span>
          </td>
          <td class="py-1 text-right whitespace-nowrap">
            <Button
              icon="i-lucide-pencil"
              size="xs"
              variant="ghost"
              @click="edit(grant)"
            />
            <Button
              icon="i-lucide-trash-2"
              size="xs"
              variant="ghost"
              color="ruby"
              @click="remove(grant)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <div class="flex flex-col gap-2 pt-4 border-t border-n-weak">
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('TABLES.GRANTS.SUBJECT') }}
        <select
          v-model="draft.subject"
          :disabled="!!editingId"
          class="w-64 px-2 py-1 text-sm border rounded border-n-weak bg-transparent text-n-slate-12 disabled:opacity-60"
        >
          <option value="" />
          <option v-for="option in subjectOptions" :key="option.value" :value="option.value">
            {{ option.group }} · {{ option.label }}
          </option>
        </select>
      </label>

      <div class="flex flex-wrap gap-3 text-xs text-n-slate-11">
        <label class="flex items-center gap-1">
          <input v-model="draft.can_read" type="checkbox" />{{ t('TABLES.GRANTS.READ') }}
        </label>
        <label class="flex items-center gap-1">
          <input v-model="draft.can_create_rows" type="checkbox" />{{ t('TABLES.GRANTS.CREATE') }}
        </label>
        <label class="flex items-center gap-1">
          <input v-model="draft.can_delete_rows" type="checkbox" />{{ t('TABLES.GRANTS.DELETE') }}
        </label>
        <label class="flex items-center gap-1">
          <input v-model="draft.can_raw_query" type="checkbox" />{{ t('TABLES.GRANTS.RAW') }}
        </label>
      </div>

      <!-- Write permission is per column: a subject can be allowed to change
           one column while being denied every other. -->
      <div class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-11">{{ t('TABLES.GRANTS.WRITABLE') }}</span>
        <div class="flex flex-wrap gap-1">
          <button
            v-for="column in columns"
            :key="column.key"
            type="button"
            class="px-2 py-0.5 text-xs border rounded border-n-weak"
            :class="
              draft.writable_columns.includes(column.key)
                ? 'bg-n-brand text-white'
                : 'text-n-slate-11'
            "
            @click="toggleColumn(column.key)"
          >
            {{ column.name }}
          </button>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <Button
          :label="editingId ? t('TABLES.GRANTS.UPDATE') : t('TABLES.GRANTS.SAVE')"
          size="sm"
          class="self-start"
          @click="save"
        />
        <Button
          v-if="editingId"
          :label="t('TABLES.GRANTS.CANCEL')"
          variant="ghost"
          size="sm"
          class="self-start"
          @click="cancelEdit"
        />
      </div>
      <p v-if="errorMessage" class="p-2 m-0 text-sm rounded text-n-ruby-11 bg-n-ruby-3">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>
